// src/components/admin/flows/ApprovalChainBuilder.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Plus, Link2, GripVertical, Trash2, ChevronDown, ChevronUp, ArrowRight
} from 'lucide-react';
import { useFlowAutomation, generateId } from '../../../contexts/FlowAutomationContext';
import { useTheme } from '../../../contexts/ThemeContext';
import EmptyState from './components/EmptyState';

const DEFAULT_STEP = () => ({
    id: generateId('approval'),
    approver_type: 'reporting_manager',
    approver_value: '',
    auto_approve_hours: null,
    escalate_hours: null,
    escalate_to: 'skip_level_manager',
});

const ApprovalChainBuilder = () => {
    const { theme } = useTheme();
    const navigate = useNavigate();
    const isDark = theme === 'dark';
    const { saveWorkflow, initNewWorkflow } = useFlowAutomation();

    const [chainName, setChainName] = useState('');
    const [triggerEvent, setTriggerEvent] = useState('leave_requested');
    const [steps, setSteps] = useState([DEFAULT_STEP()]);
    const [saving, setSaving] = useState(false);

    const inputClass = `w-full px-3 py-2 rounded-lg text-sm border ${
        isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
    } focus:ring-2 focus:ring-blue-500 focus:outline-none`;

    const labelClass = `block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`;

    const addStep = () => {
        setSteps(prev => [...prev, DEFAULT_STEP()]);
    };

    const removeStep = (id) => {
        if (steps.length <= 1) return;
        setSteps(prev => prev.filter(s => s.id !== id));
    };

    const updateStep = (id, updates) => {
        setSteps(prev => prev.map(s => s.id === id ? { ...s, ...updates } : s));
    };

    const moveStep = (index, direction) => {
        const newSteps = [...steps];
        const newIndex = index + direction;
        if (newIndex < 0 || newIndex >= newSteps.length) return;
        [newSteps[index], newSteps[newIndex]] = [newSteps[newIndex], newSteps[index]];
        setSteps(newSteps);
    };

    const handleSaveAsWorkflow = async () => {
        if (!chainName.trim()) {
            alert('Please enter a name for the approval chain');
            return;
        }

        setSaving(true);

        // Convert approval chain to a full workflow
        const workflowSteps = [
            {
                id: generateId('step'),
                type: 'trigger',
                label: 'Trigger',
                config: { event: triggerEvent, filters: {} },
                position: { x: 300, y: 50 },
            },
            ...steps.map((step, i) => ({
                id: generateId('step'),
                type: 'approval',
                label: `Approval ${i + 1}: ${step.approver_type.replace(/_/g, ' ')}`,
                config: {
                    approvers: { type: step.approver_type, value: step.approver_value },
                    approval_type: 'any',
                    auto_approve_after_hours: step.auto_approve_hours,
                    escalate_after_hours: step.escalate_hours,
                    escalate_to: step.escalate_hours ? { type: step.escalate_to, value: '' } : null,
                },
                position: { x: 300, y: 200 + i * 150 },
            })),
            {
                id: generateId('step'),
                type: 'end',
                label: 'End',
                config: {},
                position: { x: 300, y: 200 + steps.length * 150 },
            },
        ];

        // Create connections
        const connections = [];
        for (let i = 0; i < workflowSteps.length - 1; i++) {
            connections.push({
                id: generateId('conn'),
                from_step_id: workflowSteps[i].id,
                to_step_id: workflowSteps[i + 1].id,
            });
        }

        const workflow = initNewWorkflow({
            name: chainName,
            type: 'approval_chain',
            steps: workflowSteps,
            connections,
            trigger: { event: triggerEvent, filters: {} },
        });

        const result = await saveWorkflow(workflow);
        setSaving(false);

        if (result?.workflow?.id) {
            navigate(`/admin/flows/builder/${result.workflow.id}`);
        }
    };

    return (
        <div className="max-w-2xl mx-auto space-y-6">
            <div className={`rounded-xl p-6 ${isDark ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'} shadow-sm`}>
                <h2 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    Build Approval Chain
                </h2>

                {/* Chain Name */}
                <div className="mb-4">
                    <label className={labelClass}>Chain Name</label>
                    <input
                        type="text"
                        value={chainName}
                        onChange={(e) => setChainName(e.target.value)}
                        className={inputClass}
                        placeholder="e.g., Multi-level Leave Approval"
                    />
                </div>

                {/* Trigger */}
                <div className="mb-6">
                    <label className={labelClass}>Triggered By</label>
                    <select
                        value={triggerEvent}
                        onChange={(e) => setTriggerEvent(e.target.value)}
                        className={inputClass}
                    >
                        <option value="leave_requested">Leave Request</option>
                        <option value="attendance_regularization">Attendance Regularization</option>
                        <option value="employee_joined">Employee Onboarding</option>
                        <option value="employee_exit_initiated">Employee Offboarding</option>
                        <option value="manual">Manual Trigger</option>
                    </select>
                </div>

                {/* Approval Steps */}
                <div className="space-y-3">
                    <p className={`text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                        Approval Steps
                    </p>

                    {steps.map((step, index) => (
                        <div
                            key={step.id}
                            className={`relative rounded-lg border p-4 ${isDark ? 'bg-gray-700/50 border-gray-600' : 'bg-gray-50 border-gray-200'}`}
                        >
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-2">
                                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold bg-purple-100 text-purple-600 dark:bg-purple-900/40 dark:text-purple-400`}>
                                        {index + 1}
                                    </div>
                                    <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                        Step {index + 1}
                                    </span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <button
                                        onClick={() => moveStep(index, -1)}
                                        disabled={index === 0}
                                        className={`p-1 rounded disabled:opacity-30 ${isDark ? 'hover:bg-gray-600 text-gray-400' : 'hover:bg-gray-200 text-gray-500'}`}
                                    >
                                        <ChevronUp className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                        onClick={() => moveStep(index, 1)}
                                        disabled={index === steps.length - 1}
                                        className={`p-1 rounded disabled:opacity-30 ${isDark ? 'hover:bg-gray-600 text-gray-400' : 'hover:bg-gray-200 text-gray-500'}`}
                                    >
                                        <ChevronDown className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                        onClick={() => removeStep(step.id)}
                                        disabled={steps.length <= 1}
                                        className="p-1 rounded text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 disabled:opacity-30"
                                    >
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className={labelClass}>Approver</label>
                                    <select
                                        value={step.approver_type}
                                        onChange={(e) => updateStep(step.id, { approver_type: e.target.value })}
                                        className={inputClass}
                                    >
                                        <option value="reporting_manager">Reporting Manager</option>
                                        <option value="specific_user">Specific User</option>
                                        <option value="role">By Role (HR Admin)</option>
                                    </select>
                                </div>
                                <div>
                                    <label className={labelClass}>Auto-approve (hrs)</label>
                                    <input
                                        type="number"
                                        value={step.auto_approve_hours || ''}
                                        onChange={(e) => updateStep(step.id, { auto_approve_hours: e.target.value ? parseInt(e.target.value) : null })}
                                        className={inputClass}
                                        placeholder="None"
                                        min={1}
                                    />
                                </div>
                            </div>

                            {/* Connector arrow to next step */}
                            {index < steps.length - 1 && (
                                <div className="flex justify-center mt-3 -mb-6">
                                    <ArrowRight className={`w-4 h-4 rotate-90 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
                                </div>
                            )}
                        </div>
                    ))}

                    {/* Add Step */}
                    <button
                        onClick={addStep}
                        className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-lg border-2 border-dashed text-sm font-medium transition-colors ${
                            isDark
                                ? 'border-gray-600 text-gray-400 hover:border-purple-500 hover:text-purple-400'
                                : 'border-gray-300 text-gray-500 hover:border-purple-400 hover:text-purple-600'
                        }`}
                    >
                        <Plus className="w-4 h-4" />
                        Add Approval Step
                    </button>
                </div>

                {/* Save Button */}
                <div className="mt-6 flex justify-end">
                    <button
                        onClick={handleSaveAsWorkflow}
                        disabled={saving || !chainName.trim()}
                        className="flex items-center gap-2 px-5 py-2.5 bg-blue-500 text-white rounded-lg text-sm font-medium hover:bg-blue-600 disabled:opacity-50 transition-colors"
                    >
                        {saving ? 'Creating...' : 'Create Approval Chain'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ApprovalChainBuilder;
